import _ from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";

import { withCharacterContext } from "../../components/character/CharacterContext";
import { asDragSource } from "../../components/dragAndDrop/DraggableComponent";
import FormEditableLabel from "../../components/form/FormEditableLabel";
import { SUBWAY_CARD_TEXT_WIDTH } from "../../constants";
import { SUBWAY_VIEW } from "../../constants/identifiers";
import { PropTypeShapes } from "../../types";
import { getModifierKeysForEvent } from "../../utils/getModifierKeys";
import { getLabelWithUntitled, getMetricsLabelSize } from "../../utils/labelUtils";

const dragCallbacks = {
  beginDrag: _props => {
    // TODO: Do we want to set isDragging or similar...?
  },

  canDrag: (_props, dragSource) => {
    return dragSource.component.canDrag();
  },

  getDragSourceParams: props => {
    return {
      itemId: props.itemId,
      itemType: props.itemType,
      sourceView: props.viewId,
      sourceViewType: SUBWAY_VIEW,
      sourceThread: props.threadId
    };
  },

  getPreviewElement: props => renderNodePreview(props),

  getDragSourceId: props => {
    return `${props.viewId}-${props.threadId}-${props.itemId}`;
  }
};

const renderNodePreview = nodeProps => {
  const cloneProps = _.clone(nodeProps);
  cloneProps.selected = false;
  cloneProps.x = 0;
  cloneProps.y = 0;
  cloneProps.onClick = null;
  return renderNode(cloneProps, true, null);
};

const renderTextLines = lines => {
  return lines.map((l, index) => {
    return <span key={index}>{l}</span>;
  });
};

const getInitialEditingStatus = (autoEditWhenBlank, isSelected, label, isPreview, isActiveView) => {
  let result = false;
  //#!if iOS !== true
  result =
    isActiveView &&
    !isPreview &&
    autoEditWhenBlank &&
    isSelected &&
    (!label || label.trim() === getLabelWithUntitled());
  //#!endif
  return result;
};

const renderNode = (useProps, isPreview, onChangeEditing, onLabelChange, getTextSizeFunction) => {
  let classes = ["subwayNode", useProps.color];
  if (useProps.selected) {
    classes.push("selected");
  }

  const opacity = useProps.isDragging ? 0.2 : 1;

  const nodeStyle = {
    left: `${useProps.x}px`,
    top: `${useProps.y}px`,
    opacity
  };
  const textStyle = {
    left: `${useProps.titleOffset.x}px`,
    top: `${useProps.titleOffset.y}px`,
    width: `${useProps.titleMetrics.size.width}px`,
    height: `${useProps.titleMetrics.size.height}px`
  };

  const onClick = e => useProps.onClick(useProps.itemId, getModifierKeysForEvent(e), useProps.selected);

  const title = useProps.label;
  const placeholder = `${useProps.typeLabel} ${useProps.typeLabelDisplay}`;

  return (
    <div
      onClick={onClick}
      onDoubleClick={useProps.onDoubleClick}
      id={`subwayNode-${useProps.nodeId}`}
      className={classes.join(" ")}
      style={nodeStyle}
    >
      <div className={`subwayNodeDot ${useProps.junction ? "junction" : ""}`} title={title} />
      {useProps.titleMetrics.size.width > 0 ? (
        <div className="subwayNodeText" style={textStyle}>
          <FormEditableLabel
            value={title}
            displayValue={renderTextLines(useProps.titleMetrics.lines)}
            isEditing={getInitialEditingStatus(
              useProps.autoEditWhenBlank,
              useProps.selected,
              title,
              isPreview,
              useProps.isActiveView
            )}
            placeholder={placeholder}
            multiline={true}
            allowNewLine={false}
            onChange={onLabelChange}
            getSizeFunction={getTextSizeFunction}
            onChangeEditing={onChangeEditing}
            isStruckOut={useProps.isCompleted}
          />
        </div>
      ) : null}
    </div>
  );
};

class SubwayNodeView extends PureComponent {
  constructor(props, context) {
    super(props, context);

    this.onChangeEditing = this.onChangeEditing.bind(this);
    this.onLabelChange = this.onLabelChange.bind(this);
    this.getTextSizeFunction = this.getTextSizeFunction.bind(this);

    this._isEditing = false;
  }

  componentDidMount() {
    this._isEditing = getInitialEditingStatus(
      this.props.autoEditWhenBlank,
      this.props.selected,
      this.props.titleMetrics.lines.join(""),
      false,
      this.props.isActiveView
    );
  }

  canDrag() {
    // Disable the drag and drop when editing inside the node
    // The drag and drop may impact on the click event and focusing.
    return !this._isEditing && this.props.allowDrag;
  }

  onChangeEditing(value) {
    this._isEditing = value;
  }

  onLabelChange(value) {
    this.props.onLabelChange([this.props.itemId], value);
  }

  getTextSizeFunction(text) {
    return getMetricsLabelSize(
      text,
      this.props.characterContext.getWidthMap(),
      this.props.characterContext.getFontSize(),
      SUBWAY_CARD_TEXT_WIDTH
    );
  }

  render() {
    return renderNode(this.props, false, this.onChangeEditing, this.onLabelChange, this.getTextSizeFunction);
  }
}

SubwayNodeView.propTypes = {
  viewId: PropTypes.string.isRequired,
  nodeId: PropTypes.string.isRequired,
  threadId: PropTypes.string.isRequired,
  itemId: PropTypes.string.isRequired,
  itemType: PropTypes.string.isRequired,
  isActiveView: PropTypes.bool,
  selected: PropTypes.bool.isRequired,
  onLabelChange: PropTypes.func.isRequired,
  titleMetrics: PropTypeShapes.textMetrics.isRequired,
  titleOffset: PropTypeShapes.point.isRequired,
  label: PropTypes.string,
  color: PropTypes.string.isRequired,
  isCompleted: PropTypes.bool,
  icon: PropTypes.string.isRequired,
  iconColor: PropTypes.string.isRequired,
  typeLabel: PropTypes.string.isRequired,
  typeLabelDisplay: PropTypes.string.isRequired,
  junction: PropTypes.bool.isRequired,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  subwayViewSettings: PropTypeShapes.subwayViewSettings.isRequired,
  onClick: PropTypes.func,
  onDoubleClick: PropTypes.func,
  autoEditWhenBlank: PropTypes.bool.isRequired,
  allowDrag: PropTypes.bool.isRequired,

  // comes from asDragSource
  isDragging: PropTypes.bool,

  // From character context
  characterContext: PropTypes.object.isRequired
};

export default withCharacterContext(asDragSource(SubwayNodeView, SUBWAY_VIEW, dragCallbacks));
